import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import type { TranscriptEntry } from '../store/conversationSlice';
import { transcriptSpeakerLabel } from '../utils/transcriptSpeakerLabel';
import { useAlertViewer } from '../contexts/SessionViewerContext';
import { FORMING_LINE_ID, TranscriptFeed } from './training/TranscriptFeed';
import {
  STRIP_DROPDOWN_CLASS,
  STRIP_FOOTER_CLASS,
  STRIP_HEADER_CLASS,
  STRIP_PILL_CLASS,
  STRIP_PREVIEW_CLASS,
  STRIP_TITLE_GROUP_CLASS,
  StripChevron,
  StripRule,
} from './summaryStrip';

// Outer className is intentionally NOT set here — the parent (CombinedInfoBar) owns
// the wrapper styling and the `relative` positioning context so the dropdown stretches
// to the full combined-bar width via `absolute left-0 right-0 top-full`.
export const LiveTranscriptBar: React.FC = () => {
  const { transcripts: committed, participants, interimTranscript } = useSelector(
    (state: RootState) => state.conversation
  );
  // The line being spoken right now (training only) is shown after the
  // committed ones, as it arrives; it is not counted until it is committed.
  const transcripts: TranscriptEntry[] = interimTranscript?.text?.trim()
    ? [...committed, { id: FORMING_LINE_ID, timestamp: new Date().toISOString(), ...interimTranscript }]
    : committed;
  // "You" only for the viewer's own lines; a watcher is never "You".
  const viewer = useAlertViewer();
  const [isExpanded, setIsExpanded] = useState(false);
  // Tracks the speaker of the latest unread transcript while the bar is collapsed.
  // Cleared when the user opens the panel to view transcripts.
  const [newTranscriptSpeaker, setNewTranscriptSpeaker] = useState<'customer' | 'salesperson' | null>(null);
  const [isFlashing, setIsFlashing] = useState(false);
  // The newest LIVE line the rep has seen (opened the bar on). Restored
  // history never counts as new, and an id survives the store's 50-line cap
  // where a count would not.
  const seenLiveLineIdRef = useRef<string | null>(null);

  const latestTranscript = transcripts.length > 0 ? transcripts[transcripts.length - 1] : null;
  const latestLiveLine = [...committed].reverse().find((line) => !line.isRestored) ?? null;
  const latestLiveLineId = latestLiveLine?.id ?? null;
  const latestLiveSpeaker = latestLiveLine?.speaker ?? null;

  // Highlight the collapsed bar when a new live line arrives while it's closed.
  // Badge flashes for 3 pulses (3 × 0.4s = 1.2s) then stays red until user opens.
  useEffect(() => {
    if (isExpanded || !latestLiveLineId || latestLiveLineId === seenLiveLineIdRef.current) return;
    if (latestLiveSpeaker === 'customer' || latestLiveSpeaker === 'salesperson') {
      setNewTranscriptSpeaker(latestLiveSpeaker);
      setIsFlashing(true);
      const timer = setTimeout(() => setIsFlashing(false), 1200);
      return () => clearTimeout(timer);
    }
  }, [latestLiveLineId, latestLiveSpeaker, isExpanded]);

  const handleToggle = () => {
    setIsExpanded((prev) => {
      const opening = !prev;
      if (opening) {
        // Mark all current transcripts as seen and clear the new-message indicator
        seenLiveLineIdRef.current = latestLiveLineId;
        setNewTranscriptSpeaker(null);
      }
      return opening;
    });
  };

  const isRepHighlight = !isExpanded && newTranscriptSpeaker === 'salesperson';

  return (
    <>
      {/* Collapsed bar — always visible.
          When a new transcript arrives while collapsed, the row is tinted for the speaker:
          a rep's line signature-tint, a customer's paper (as on macOS).
          The tint clears as soon as the user opens the panel. */}
      <div
        className={`${STRIP_HEADER_CLASS} ${
          isRepHighlight
            ? 'bg-signature-tint hover:bg-surface-sunken'
            : !isExpanded && newTranscriptSpeaker === 'customer'
            ? 'bg-paper hover:bg-surface-subtle'
            : 'hover:bg-surface-subtle'
        }`}
        onClick={handleToggle}
      >
        <div className={STRIP_TITLE_GROUP_CLASS}>
          <h3 className={`text-xs font-semibold ${isRepHighlight ? 'text-signature-strong' : 'text-ink'}`}>
            Transcript
          </h3>
          <span className={`${STRIP_PILL_CLASS} ${
            !isExpanded && newTranscriptSpeaker
              ? `bg-error text-white${isFlashing ? ' animate-badge-flash' : ''}`
              : 'bg-paper text-ink-secondary'
          }`}>
            {committed.length}
          </span>
        </div>

        <StripRule />

        {/* Latest entry preview */}
        {latestTranscript ? (
          <div className={STRIP_PREVIEW_CLASS}>
            <span className="flex-shrink-0 font-medium text-ink-secondary">
              {transcriptSpeakerLabel(latestTranscript, participants, viewer)}:
            </span>
            <span className="truncate text-ink-muted">
              {latestTranscript.text.length > 60
                ? `${latestTranscript.text.substring(0, 60)}\u2026`
                : latestTranscript.text}
            </span>
          </div>
        ) : (
          <span className="min-w-0 flex-1 truncate text-[11px] text-ink-muted">No transcripts yet</span>
        )}

        {/* Expand / collapse chevron */}
        <button
          className="flex-shrink-0 p-0.5 text-ink-muted transition-colors hover:text-ink-secondary"
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
        >
          <StripChevron isExpanded={isExpanded} />
        </button>
      </div>

      {/* Expanded dropdown — positioned via absolute on the CombinedInfoBar's relative container */}
      {isExpanded && (
        <div className={STRIP_DROPDOWN_CLASS}>
          <TranscriptFeed
            transcripts={transcripts}
            participants={participants}
            className="max-h-accordion bg-surface"
          />

          {committed.length > 0 && (
            <div className={STRIP_FOOTER_CLASS}>
              {committed.length} transcript{committed.length === 1 ? '' : 's'} recorded
            </div>
          )}
        </div>
      )}
    </>
  );
};
